import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { colors } from '@/styles/css/colors';
import type { SummaryCardsProps, SummaryCardDefinition, SummaryCardMetrics } from './types';

/**
 * Card definitions for summary metrics display.
 * Defines the order and labels for each metric card.
 */
const CARD_DEFINITIONS: ReadonlyArray<SummaryCardDefinition> = [
  { key: 'totalApiCalls', label: 'Total API Calls' },
  { key: 'avgDailyUsers', label: 'Avg Daily Active Users' },
  { key: 'totalUniqueUsers', label: 'Total Unique Users' },
  { key: 'latestWeeklyActiveUsers', label: 'Latest Weekly Active Users' },
  { key: 'daysTracked', label: 'Days Tracked' },
] as const;

/**
 * Summary cards component displaying key metrics.
 * Uses named export to match codebase conventions.
 */
export const SummaryCards: React.FC<SummaryCardsProps> = ({ cards }) => {
  return (
    <Box 
      sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}
      role="region"
      aria-label="Summary metrics"
    >
      {CARD_DEFINITIONS.map(({ key, label }) => (
        <Paper
          key={key}
          variant="outlined"
          sx={{
            flex: '1 1 160px',
            minWidth: 160,
            p: 2,
            textAlign: 'center',
            borderTop: `3px solid ${colors.DASHBOARDGRAPH}`,
          }}
          role="article"
          aria-label={`${label}: ${cards[key]}`}
        >
          <Typography 
            variant="h5" 
            sx={{ fontWeight: 700 }}
            component="p"
            aria-label={`Value: ${cards[key]}`}
          >
            {cards[key]}
          </Typography>
          <Typography 
            variant="caption" 
            color="text.secondary"
            component="p"
          >
            {label}
          </Typography>
        </Paper>
      ))}
    </Box>
  );
};
